import classNames from 'classnames';
import { omit } from 'lodash-es';
import { forwardRef } from 'react';
import { Tooltip } from '../../tooltip';
import type { IconComponent } from '../../types';

type NativeButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

type ButtonVariant = 'CtaPrimary' | 'CtaSecondary' | 'CtaTertiary' | 'CtaDanger' | 'CtaGhost' | 'ActionQuiet' | 'ActionNotQuiet';

type ButtonProps = NativeButtonProps & {
  icon?: IconComponent;
  hideLabel?: boolean;
  variant: ButtonVariant;
  tooltip?: string;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props: ButtonProps, ref) => (
  <Tooltip content={props.tooltip}>
    <button ref={ref} {...omit(props, 'children', 'hideLabel', 'icon', 'variant', 'tooltip')} className={classNames(
      'inline-flex items-center flex-nowrap',
      props.className,
      'tds-button',
      {
        'tds-button-cta tds-button-cta-primary': props.variant === 'CtaPrimary',
        'tds-button-cta tds-button-cta-secondary': props.variant === 'CtaSecondary',
        'tds-button-cta tds-button-cta-tertiary': props.variant === 'CtaTertiary',
        'tds-button-cta tds-button-cta-danger': props.variant === 'CtaDanger',
        'tds-button-cta tds-button-cta-ghost': props.variant === 'CtaGhost',
        'tds-button-action-quiet': props.variant === 'ActionQuiet',
        'tds-button-action-not-quiet': props.variant === 'ActionNotQuiet',
      },
      { 'tds-icon-only': props.hideLabel },
    )}>
      {/* TBITOP-1529 werden die Design Tokens (hier Button Size) ge-treeshaket? */}
      {props.icon ? <props.icon size={'1.5rem'} /> : null}
      <span className={classNames('whitespace-nowrap', { 'sr-only': props.hideLabel })}>{props.children}</span>
    </button>
  </Tooltip>
));

type NativeDivProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type ButtonSurrogateProps = NativeDivProps & {
  icon?: IconComponent;
  hideLabel?: boolean;
  variant: ButtonVariant;
};

/* ButtonSurrogate currently cannot be displayed disabled etc., because all the native Button Props are not emulated yet*/
const ButtonSurrogate = forwardRef<HTMLDivElement, ButtonSurrogateProps>((props, ref) => (
  <div ref={ref} {...omit(props, 'children', 'hideLabel', 'icon', 'variant')} className={classNames(
    'inline-flex items-center flex-nowrap',
    props.className,
    'tds-button',
    {
      'tds-button-cta tds-button-cta-primary': props.variant === 'CtaPrimary',
      'tds-button-cta tds-button-cta-secondary': props.variant === 'CtaSecondary',
      'tds-button-cta tds-button-cta-tertiary': props.variant === 'CtaTertiary',
      'tds-button-cta tds-button-cta-danger': props.variant === 'CtaDanger',
      'tds-button-cta tds-button-cta-ghost': props.variant === 'CtaGhost',
      'tds-button-action-quiet': props.variant === 'ActionQuiet',
      'tds-button-action-not-quiet': props.variant === 'ActionNotQuiet',
    },
    { 'tds-icon-only': props.hideLabel },
  )}>
    {/* TBITOP-1529 werden die Design Tokens (hier Button Size) ge-treeshaket? */}
    {props.icon ? <props.icon size={'1.5rem'} /> : null}
    <span className={classNames('whitespace-nowrap', { 'sr-only': props.hideLabel })}>{props.children}</span>
  </div>
));

type CleanButtonProps = Omit<ButtonProps, 'variant' | 'ref'>;

export const ButtonCtaPrimary = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="CtaPrimary" />
));

export const ButtonCtaSecondary = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="CtaSecondary" />
));

export const ButtonCtaTertiary = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="CtaTertiary" />
));

export const ButtonCtaGhost = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="CtaGhost" />
));

export const ButtonCtaDanger = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="CtaDanger" />
));

export const ButtonActionQuiet = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="ActionQuiet" />
));

export const ButtonActionNotQuiet = forwardRef<HTMLButtonElement, CleanButtonProps>((props: CleanButtonProps, ref) => (
  <Button {...props} ref={ref} variant="ActionNotQuiet" />
));

type CleanButtonSurrogateProps = Omit<ButtonSurrogateProps, 'variant' | 'ref'>;

export const ButtonCtaPrimarySurrogate = forwardRef<HTMLDivElement, CleanButtonSurrogateProps>((props, ref) => (
  <ButtonSurrogate {...props} ref={ref} variant="CtaPrimary" />
));

export const ButtonCtaGhostSurrogate = forwardRef<HTMLDivElement, CleanButtonSurrogateProps>((props, ref) => (
  <ButtonSurrogate {...props} ref={ref} variant="CtaGhost" />
));

export const ButtonActionQuietSurrogate = forwardRef<HTMLDivElement, CleanButtonSurrogateProps>((props, ref) => (
  <ButtonSurrogate {...props} ref={ref} variant="ActionQuiet" />
));

export const ButtonActionNotQuietSurrogate = forwardRef<HTMLDivElement, CleanButtonSurrogateProps>((props, ref) => (
  <ButtonSurrogate {...props} ref={ref} variant="ActionNotQuiet" />
));
